import { FilePicker } from "@/components/FilePicker";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import {
  createSignatureVersion,
  getSignatureImage,
  listSignatureVersions,
  listSignatures,
  type Signature,
  type SignatureVersion,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { useObjectURL } from "@/components/useObjectURL";
import { effectiveFromFor, startOfLocalDay } from "@/features/signatures/dates";

const detailApi = getRouteApi("/staff/signatures/$signatureId");

export function SignatureDetailPage() {
  const { signatureId } = detailApi.useParams();
  // There is no single-signature endpoint; the list is small and shared with the list screen.
  const signatures = useQuery({ queryKey: ["signatures"], queryFn: listSignatures });
  const signature = signatures.data?.find((s) => s.id === signatureId);

  if (signatures.isSuccess && !signature) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Signature not found" detail="No signature matches this link." />
        <Link to="/signatures" className="text-sm font-medium text-zinc-950 underline">
          Back to signatures
        </Link>
      </section>
    );
  }
  if (!signature) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Signature" detail={signatureId} />
        <ErrorBanner error={signatures.error} />
        {signatures.isLoading ? <EmptyState title="Loading signature" detail="Fetching the signatory." /> : null}
      </section>
    );
  }
  return <SignatureView signature={signature} />;
}

function SignatureView({ signature }: { signature: Signature }) {
  const { can } = useSession();
  const versions = useQuery({
    queryKey: ["signature-versions", signature.id],
    queryFn: () => listSignatureVersions(signature.id),
  });
  const items = versions.data ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader title={signature.signatory_name} detail={signature.role_title ?? "No role title"} />
      <ErrorBanner error={versions.error} />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <div className="rounded-lg border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 px-4 py-3">
            <h2 className="text-sm font-semibold">Versions</h2>
            <p className="mt-1 text-xs text-zinc-500">
              A certificate uses the version whose dates cover its issue date; a re-issue uses the version covering the original's issue date.
            </p>
          </div>
          {versions.isLoading ? (
            <EmptyState title="Loading versions" detail="Fetching signature images." />
          ) : items.length === 0 ? (
            <EmptyState title="No signature image yet" detail="Upload one to use this signatory on certificates." />
          ) : (
            <ol aria-label="Versions" className="divide-y divide-zinc-100">
              {items.map((version) => (
                <VersionItem key={version.id} version={version} />
              ))}
            </ol>
          )}
        </div>
        {can("signature.manage") ? (
          <UploadVersionForm signature={signature} />
        ) : null}
      </div>
    </section>
  );
}

function VersionItem({ version }: { version: SignatureVersion }) {
  const image = useQuery({
    queryKey: ["signature-image", version.signature_id, version.id],
    queryFn: () => getSignatureImage(version.signature_id, version.id),
    // A version's image never changes.
    staleTime: Infinity,
  });
  const url = useObjectURL(image.data);
  const current = !version.effective_to;
  return (
    <li className="flex flex-wrap items-center gap-4 px-4 py-3">
      <div className="min-w-48 flex-1">
        <h3 className="text-sm font-medium">
          Version {version.version_no}
          {current ? (
            <span className="ml-2 rounded-full bg-zinc-950 px-2 py-0.5 text-xs font-medium text-white">Current</span>
          ) : null}
        </h3>
        <p className="mt-1 text-xs text-zinc-500">
          {formatDate(version.effective_from)} to {version.effective_to ? formatDate(version.effective_to) : "now"}
        </p>
      </div>
      <div className="grid h-20 w-56 place-items-center rounded-md border border-zinc-200 bg-zinc-50">
        {url ? (
          <img src={url} alt={`Signature version ${version.version_no}`} className="max-h-full max-w-full object-contain" />
        ) : image.isError ? (
          <span className="text-xs text-red-700">Image could not be loaded</span>
        ) : (
          <span className="text-xs text-zinc-500">Loading image...</span>
        )}
      </div>
    </li>
  );
}

function UploadVersionForm({ signature }: { signature: Signature }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  // No default: the date decides which certificates this image signs, so it is chosen deliberately.
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  const upload = useMutation({
    mutationFn: ({ image, date, until }: { image: File; date: string; until: string }) =>
      until
        ? createSignatureVersion(signature.id, image, effectiveFromFor(date), startOfLocalDay(until))
        : createSignatureVersion(signature.id, image, effectiveFromFor(date)),
    onSuccess: async () => {
      setFile(null);
      setEffectiveFrom("");
      setEffectiveTo("");
      await queryClient.invalidateQueries({ queryKey: ["signature-versions", signature.id] });
      await queryClient.invalidateQueries({ queryKey: ["signatures"] });
    },
  });

  const field = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="h-fit space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Upload a new version</h2>
      <FilePicker
        label="Signature image"
        accept="image/png,image/jpeg"
        extensions={[".png", ".jpg", ".jpeg"]}
        typeError="Only PNG or JPEG images can be uploaded"
        maxBytes={5 << 20}
        hint="PNG or JPEG, up to 5 MB"
        thumbnail
        value={file}
        onChange={(next) => {
          setFile(next);
          upload.reset();
        }}
      />
      <label className="block text-sm">
        <span className="font-medium text-zinc-700">Effective from</span>
        <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className={field} />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-zinc-700">Ends before (optional)</span>
        <input type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} className={field} />
      </label>
      <p className="text-xs text-zinc-500">The version stops covering certificates from the start of this day.</p>
      <p className="text-xs text-zinc-500">
        Today takes effect immediately; any other day, past or future, from the start of that day. A past range
        must fit a gap between versions; it then signs re-issues of certificates from that period.
      </p>
      <ToolbarButton
        disabled={!file || effectiveFrom === "" || upload.isPending}
        onClick={() => file && upload.mutate({ image: file, date: effectiveFrom, until: effectiveTo })}
      >
        {upload.isPending ? "Uploading..." : "Upload version"}
      </ToolbarButton>
      {upload.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Version {upload.data.version_no} uploaded.
        </p>
      ) : null}
      <ErrorBanner error={upload.error} />
    </div>
  );
}
